
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  deleteDoc,
  collection,
  serverTimestamp,
  increment,
  writeBatch
} from '../firebase';
import { auth, db } from '../firebase';
import { CartItem, Product, UserProfile, UserSettings } from '../types';
import { useNotification } from '../components/Notification';

interface UserContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  addToCart: (product: Product, options?: { color?: string; size?: string }) => Promise<void>;
  removeFromCart: (productId: number | string) => Promise<void>;
  updateCartQuantity: (productId: number | string, delta: number) => Promise<void>;
  toggleCartSelection: (productId: number | string) => Promise<void>;
  updateProfileName: (name: string) => Promise<void>;
  updateProfilePhoto: (base64Image: string) => Promise<void>;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
  updateProfileDetails: (details: any) => Promise<void>;
  clearCart: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const [localCart, setLocalCart] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('guestCart');
    return saved ? JSON.parse(saved) : [];
  });

  // Sync localCart to localStorage
  useEffect(() => {
    localStorage.setItem('guestCart', JSON.stringify(localCart));
  }, [localCart]);

  // 1. Listen for Auth State
  useEffect(() => {
    let mounted = true;
    const unsubscribeAuth = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        if (mounted) {
          setUser(firebaseUser);
          if (!firebaseUser) {
            setProfileData(null);
            setCartItems([]);
            // When logging out, clear Firestore cart items but keep localCart if initialized
            setLoading(false);
          } else if (firebaseUser.isAnonymous) {
            // For anonymous users, don't wait for profile if it takes too long
            // but still attempt to listen.
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        if (mounted) {
          setLoading(false);
        }
      }
    });
    return () => {
      mounted = false;
      unsubscribeAuth();
    };
  }, []);

  // 2. Listen for User Profile Document
  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);

    // Ensure profile exists
    getDoc(userRef).then((snap) => {
      if (!snap.exists()) {
        const newProfile: Partial<UserProfile> = {
          uid: user.uid,
          displayName: user.displayName || 'مستخدم جديد',
          email: user.email || '',
          photoURL: user.photoURL || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          settings: { theme: 'light', notifications: true },
          provider: user.providerData[0]?.providerId || 'email'
        };
        setDoc(userRef, newProfile, { merge: true }).catch(err => console.error("Error creating profile:", err));
      } else {
        updateDoc(userRef, { lastLogin: serverTimestamp() }).catch(err => console.error("Error updating last login:", err));
      }
    }).catch(err => {
      console.error("Error checking profile existence:", err);
      // Only log, do not block app
    });

    const unsubscribeProfile = onSnapshot(userRef, (docSnap) => {
      try {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const partialProfile = data as UserProfile;
          setProfileData(partialProfile);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error processing profile snapshot:", error);
        setLoading(false);
      }
    }, (error) => {
      console.error("Profile snapshot error:", error.code, error.message);
      setLoading(false);
    });

    return () => unsubscribeProfile();
  }, [user]);

  // 3. Listen for Cart Subcollection
  useEffect(() => {
    if (!user) return;

    const cartCollectionRef = collection(db, 'users', user.uid, 'cart');

    const unsubscribeCart = onSnapshot(cartCollectionRef, (snapshot) => {
      try {
        const items: CartItem[] = [];
        snapshot.forEach((doc) => {
          items.push({ id: Number(doc.id), ...doc.data() } as CartItem);
        });
        // Sort items by addedAt if available to maintain order
        items.sort((a: any, b: any) => {
          const timeA = a.addedAt?.toMillis ? a.addedAt.toMillis() : 0;
          const timeB = b.addedAt?.toMillis ? b.addedAt.toMillis() : 0;
          return timeB - timeA; // Newest first
        });
        setCartItems(items);
        setLoading(false);
      } catch (error) {
        console.error("Error processing cart snapshot:", error);
        setLoading(false);
      }
    }, (error) => {
      console.error("Cart snapshot error:", error.code, error.message);
      // If permission denied, likely rules issue or auth stale. 
      // We stop loading to show UI (empty cart) rather than spinner forever.
      setLoading(false);
    });

    return () => unsubscribeCart();
  }, [user]);

  // 4. Merge localCart into Firestore when user logs in
  useEffect(() => {
    if (user && localCart.length > 0) {
      const mergeCart = async () => {
        try {
          const batch = writeBatch(db);
          for (const item of localCart) {
            const cartItemRef = doc(db, 'users', user.uid, 'cart', item.id.toString());
            // We use setDoc with merge to avoid overwriting existing items or just use increment if it exists
            // For simplicity, let's just add them. If they exist, they'll be updated.
            batch.set(cartItemRef, {
              ...item,
              addedAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            }, { merge: true });
          }
          await batch.commit();
          setLocalCart([]); // Clear local cart after successful merge
          showNotification("تم دمج منتجات السلة مع حسابك", "success");
        } catch (error) {
          console.error("Error merging cart:", error);
        }
      };
      mergeCart();
    }
  }, [user, localCart.length]);

  // Combine profile data and cart items
  const profile: UserProfile | null = profileData
    ? { ...profileData, cart: user ? cartItems : localCart }
    : (user ? null : { uid: 'guest', displayName: 'ضيف', email: '', photoURL: '', cart: localCart } as any);

  // --- Cart Operations ---

  const addToCart = async (product: Product, options?: { color?: string; size?: string }) => {
    if (!product || !product.id) {
      console.error("Cannot add to cart: Product ID is missing!", product);
      showNotification("خطأ في بيانات المنتج. يرجى تحديث الصفحة.", "error");
      return false;
    }

    // Use a composite ID to allow multiple colors of the same product
    const colorSuffix = options?.color ? `_${options.color}` : "";
    const cartItemId = `${product.id}${colorSuffix}`;

    if (!user) {
      // Guest logic
      setLocalCart(prev => {
        const existing = prev.find(item => item.id === cartItemId);
        if (existing) {
          return prev.map(item => item.id === cartItemId ? { ...item, quantity: item.quantity + 1 } : item);
        }
        const newItem: CartItem = {
          ...product,
          id: cartItemId as any,
          productId: product.id,
          quantity: 1,
          selected: true,
          addedAt: new Date() as any,
          name: product.name || "Unknown Product",
          price: product.price || 0,
          image: product.image || "",
          selectedColor: options?.color || "",
          selectedSize: options?.size || ""
        } as any;
        return [newItem, ...prev];
      });
      showNotification("تمت إضافة المنتج للسلة", "success");
      return true;
    }
    const cartItemRef = doc(db, 'users', user.uid, 'cart', cartItemId);

    try {
      const docSnap = await getDoc(cartItemRef);

      if (docSnap.exists()) {
        await updateDoc(cartItemRef, {
          quantity: increment(1),
          selectedColor: options?.color || docSnap.data().selectedColor || "",
          selectedSize: options?.size || docSnap.data().selectedSize || "",
          updatedAt: serverTimestamp()
        });
      } else {
        const newItem: any = {
          ...product,
          id: cartItemId, // Store the composite ID as the item ID
          productId: product.id, // Keep original product ID
          quantity: 1,
          selected: true,
          addedAt: serverTimestamp(),
          name: product.name || "Unknown Product",
          price: product.price || 0,
          image: product.image || "",
          selectedColor: options?.color || "",
          selectedSize: options?.size || ""
        };

        await setDoc(cartItemRef, newItem);
      }
      return true;
    } catch (error: any) {
      console.error("Error adding to cart:", error.code, error.message);
      if (error.code === 'permission-denied') {
        showNotification("خطأ في الصلاحيات. يرجى إعادة تسجيل الدخول.", "error");
      } else {
        showNotification("فشل إضافة المنتج للسلة. يرجى المحاولة لاحقاً.", "error");
      }
      return false;
    }
  };

  const removeFromCart = async (cartItemId: number | string) => {
    if (!user) {
      setLocalCart(prev => prev.filter(item => item.id !== cartItemId));
      return;
    }
    try {
      const cartItemRef = doc(db, 'users', user.uid, 'cart', cartItemId.toString());
      await deleteDoc(cartItemRef);
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const updateCartQuantity = async (cartItemId: number | string, delta: number) => {
    if (!user) {
      setLocalCart(prev => prev.map(item => {
        if (item.id === cartItemId) {
          const newQty = item.quantity + delta;
          return newQty >= 1 ? { ...item, quantity: newQty } : item;
        }
        return item;
      }));
      return;
    }
    try {
      const cartItemRef = doc(db, 'users', user.uid, 'cart', cartItemId.toString());
      const snap = await getDoc(cartItemRef);
      if (snap.exists()) {
        const currentQty = snap.data().quantity || 1;
        const newQty = currentQty + delta;
        if (newQty < 1) return;

        await updateDoc(cartItemRef, { quantity: newQty });
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const toggleCartSelection = async (cartItemId: number | string) => {
    if (!user) {
      setLocalCart(prev => prev.map(item =>
        item.id === cartItemId ? { ...item, selected: !item.selected } : item
      ));
      return;
    }
    try {
      const cartItemRef = doc(db, 'users', user.uid, 'cart', cartItemId.toString());
      const snap = await getDoc(cartItemRef);
      if (snap.exists()) {
        await updateDoc(cartItemRef, { selected: !snap.data().selected });
      }
    } catch (error) {
      console.error("Error toggling selection:", error);
    }
  };

  const clearCart = async () => {
    // Clear local cart for everyone
    setLocalCart([]);
    localStorage.removeItem('guestCart');

    if (!user) return;
    try {
      const batch = writeBatch(db);
      // We use the cartItems from state (Firestore)
      cartItems.forEach(item => {
        const ref = doc(db, 'users', user.uid, 'cart', item.id.toString());
        batch.delete(ref);
      });
      await batch.commit();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const updateProfileName = async (name: string) => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        name,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error updating name:", error);
      throw error;
    }
  };

  const updateProfilePhoto = async (base64Image: string) => {
    if (!user) return;

    try {
      // 1. Try Get API Key from Firestore securely
      let apiKey = '';
      try {
        const configRef = doc(db, 'config', 'imgbb');
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          const data = configSnap.data();
          // Access fields robustly based on screenshot ('apikey' or 'api key' or 'apiKey')
          apiKey = data.apikey || data.apiKey || data['api key'];
        }
      } catch (err) {
        console.warn('Firestore config fetch failed', err);
      }

      // 2. Fallback to Environment Variable if Firestore key is missing
      if (!apiKey) {
        // Safe access using optional chaining to prevent crash if env is missing or undefined
        const meta = import.meta as any;
        apiKey = meta.env?.VITE_IMGBB_API_KEY;
      }

      if (!apiKey) {
        throw new Error("مفتاح API غير موجود. يرجى إضافته في Firestore (config/imgbb/apikey) أو ملف .env (VITE_IMGBB_API_KEY)");
      }

      // 3. Convert base64 to Blob for efficient FormData upload
      const response = await fetch(base64Image);
      const blob = await response.blob();

      // Double check size client-side (redundant but safe)
      if (blob.size > 5 * 1024 * 1024) {
        throw new Error("حجم الملف أكبر من 5 ميجابايت");
      }

      // 4. Upload to ImgBB
      const formData = new FormData();
      formData.append("image", blob);

      const uploadRes = await fetch(`https://api.imgbb.com/1/upload?key=${apiKey}`, {
        method: 'POST',
        body: formData
      });

      const result = await uploadRes.json();

      if (!result.success) {
        throw new Error(result.error?.message || "فشل رفع الصورة");
      }

      const imageUrl = result.data.url;

      // 5. Update Firestore Profile
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        photoURL: imageUrl,
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error in updateProfilePhoto:', error);
      throw error;
    }
  };

  const updateSettings = async (settings: Partial<UserSettings>) => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const currentSettings = snap.data().settings || {};
        await updateDoc(userRef, {
          settings: { ...currentSettings, ...settings },
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  };

  const updateProfileDetails = async (details: any) => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...details,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error updating profile details:", error);
      throw error;
    }
  };

  return (
    <UserContext.Provider value={{
      user,
      profile,
      loading,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      toggleCartSelection,
      updateProfileName,
      updateProfilePhoto,
      updateSettings,
      updateProfileDetails,
      clearCart
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
